import React, { useState } from 'react';

import { track, useMutation, useQuery } from '@betterment/js-runtime';
import { Box, Text } from '@betterment/samba-react';
import { Button, Flex, Modal } from '@betterment/style-closet-react';

import { useFlash } from '../../../Shared/contexts/FlashContext';
import { urlBuilder } from '../../../Shared/urlBuilder';
import { DetailedTransaction, TransactionStatus } from '../../data';
import { useAccount } from '../AccountProvider';
import { useTransactions } from '../TransactionsContext';

interface Props {
  accountType: string;
  transaction: DetailedTransaction;
  onClose: () => void;
}

const TransactionCancellationModal: React.FC<React.PropsWithChildren<Props>> = ({
  accountType,
  transaction,
  onClose,
}) => {
  const [shouldShowRetry, setShouldShowRetry] = useState(false);
  const { setFlashValues } = useFlash();
  const [postCancelTransaction, { loading: isLoading }] = useMutation(
    urlBuilder.createDetailedTransactionCancellation(transaction.id)
  );
  const typeDescription = transaction.type.description.toLowerCase();
  const { account } = useAccount();
  const { replaceTransaction: _replaceTransaction } = useTransactions(null, { skip: true });
  const { refetch: fetchTransaction } = useQuery<DetailedTransaction>(
    urlBuilder.apiV2CheckingDetailedTransaction.absolute({ accountId: account.id, transactionId: transaction.id }),
    { skip: true }
  );

  const replaceTransaction = async () => {
    let newTransaction: DetailedTransaction;

    try {
      const { data } = await fetchTransaction();
      if (!data) throw new Error('client error');
      newTransaction = data;
    } catch {
      newTransaction = {
        ...transaction,
        status: TransactionStatus.Voided,
        cancelability: { ...transaction.cancelability, eligible: false },
      };
    }

    _replaceTransaction(newTransaction);
  };

  const onClick = async () => {
    track('ElementClicked', { name: `CancelTransaction-${accountType}`, module: 'CheckingTransactionDetails' });
    const response = await postCancelTransaction();
    if (!response.errors) {
      await replaceTransaction();
      setFlashValues({ variant: 'success', message: `Your ${typeDescription} has been canceled.` });
      onClose();
    } else {
      setShouldShowRetry(true);
    }
  };

  return (
    <Modal closeHandler={onClose} label="transaction cancellation" contentWidth={['100%', '480px']} variant="new">
      <Box
        css={{
          paddingBlock: {
            base: 'space.200',
            sm: 'space.300',
          },

          paddingInline: {
            base: 'space.200',
            sm: 'space.300',
          },
        }}
      >
        {shouldShowRetry ? (
          <Flex alignItems="left" flexDirection="column">
            <Text
              css={{
                fontSize: 'fontSize.300',
                fontWeight: 'bold',
                paddingBottom: 'space.200',
              }}
            >
              We were unable to cancel your {typeDescription}.
            </Text>
            <Text
              css={{
                paddingBottom: 'space.400',
              }}
            >
              Something went wrong. Please try again.
            </Text>
            <Flex alignItems="left">
              <Button mr={6} onClick={onClick} disabled={isLoading}>
                Try again
              </Button>
              <Button variant="secondary" onClick={onClose}>
                Never mind
              </Button>
            </Flex>
          </Flex>
        ) : (
          <Flex alignItems="left" flexDirection="column">
            <Text
              css={{
                fontSize: 'fontSize.300',
                fontWeight: 'bold',
                paddingBottom: 'space.200',
              }}
            >
              Are you sure you want to cancel this {typeDescription}?
            </Text>
            <Text
              css={{
                paddingBottom: 'space.400',
              }}
            >
              This can't be undone.
            </Text>
            <Flex alignItems="left">
              <Button mr={6} onClick={onClick} disabled={isLoading}>
                Cancel {typeDescription}
              </Button>
              <Button variant="secondary" onClick={onClose}>
                Never mind
              </Button>
            </Flex>
          </Flex>
        )}
      </Box>
    </Modal>
  );
};

export default TransactionCancellationModal;
